import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { FaqSection } from "./Faq";
import { Reveal } from "./anim";
import { DEFENCE, DEFENCE_FAQ, IMAGES, SINCE, breadcrumb, faqSchema } from "./marketingData";

export default function DefencePage() {
  return (
    <div className="bg-[#1A1A18] text-white font-body antialiased overflow-x-hidden" data-testid="defence-page">
      <Seo
        title={`Indian Defence Logistics — Tanks, Ammunition & Explosive Vans | ITO since ${SINCE}`}
        description={String(DEFENCE.body || "").slice(0, 180)}
        path="/defence"
        image={IMAGES.tankTrailer}
        imageAlt="Indian Army tank on a heavy low-bed trailer"
        keywords="defence logistics india, defence cargo transport, tank transport india, ammunition transport india, explosive van, drone transport india, military logistics india"
        jsonLd={[
          {
            "@context": "https://schema.org", "@type": "Service",
            serviceType: "Defence logistics & explosive/ammunition transport",
            provider: { "@type": "Organization", name: "Indian Transport Organisation" },
            areaServed: "IN", description: String(DEFENCE.body || ""),
          },
          breadcrumb([{ name: "Home", path: "/" }, { name: "Defence", path: "/defence" }]),
          faqSchema(DEFENCE_FAQ),
        ]}
      />
      <MarketingNav heroDark />

      <section className="pt-36 pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3">Indian defence logistics · since {SINCE}</p>
              <h1 className="font-heading font-black tracking-tight text-4xl md:text-6xl leading-[1.05]">{DEFENCE.title}</h1>
              <p className="mt-5 text-white/75 leading-relaxed">{DEFENCE.body}</p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-4">
                {(DEFENCE.points || []).map((p) => (
                  <li key={p} className="flex items-center gap-3 text-sm">
                    <ShieldCheck className="w-5 h-5 text-[#C84B31] shrink-0" /> {p}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3 mt-9">
                <Link to="/services/defence-logistics" data-testid="defence-service-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-[#C84B31] px-6 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors">
                  Move defence cargo <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/fleet/explosive-van" data-testid="defence-fleet-cta"
                  className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white hover:bg-white hover:text-[#1A1A18] transition-colors">
                  See explosive vans
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden h-64 col-span-2">
                  <img src={IMAGES.tankTrailer} alt="Indian Army tank on a heavy low-bed trailer" className="w-full h-full object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden h-48">
                  <img src={IMAGES.defenceDrone} alt="Military drone prepared for road transport in India" className="w-full h-full object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden h-48">
                  <img src={IMAGES.explosiveVan} alt="Explosive van with army escort on an Indian highway" className="w-full h-full object-cover" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <FaqSection faqs={DEFENCE_FAQ} dark title="Defence logistics — frequently asked questions" testid="defence-faq" />

      <MarketingFooter />
    </div>
  );
}
